import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import { Logo } from './Logo';

const NAV_ITEMS = [
  { label: 'Women', to: '/category/women' },
  { label: 'Men', to: '/category/men' },
  { label: 'Kids', to: '/category/kids' },
  { label: 'Jewelry', to: '/category/jewelry' },
  { label: 'Bags', to: '/category/bags' },
  { label: 'Accessories', to: '/category/accessories' },
];

const ACCOUNT_ITEMS = [
  { label: 'Search', to: '/search' },
  { label: 'My Profile', to: '/profile' },
  { label: 'My Orders', to: '/orders' },
  { label: 'Wishlist', to: '/wishlist' },
];

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.25 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const panelVariants = {
  hidden: { x: '100%' },
  visible: {
    x: 0,
    transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] as [number, number, number, number] },
  },
  exit: {
    x: '100%',
    transition: { duration: 0.35, ease: [0.7, 0, 0.84, 0] as [number, number, number, number] },
  },
};

const listVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.05, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] as [number, number, number, number] },
  },
};

/**
 * MobileNav — full-screen Framer Motion drawer (PRD §8.1).
 *
 * Slides in from the right, overlay fades in behind. Nav items stagger-reveal
 * top-to-bottom. Toggled via `useUIStore.openMobileNav / closeMobileNav`.
 * Closes automatically on route change so the drawer never lingers.
 */
export function MobileNav() {
  const isOpen = useUIStore((s) => s.isMobileNavOpen);
  const close = useUIStore((s) => s.closeMobileNav);
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();

  // Auto-close on route change.
  useEffect(() => {
    if (isOpen) close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Close on Escape key.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <div role="dialog" aria-modal="true" aria-label="Mobile navigation">
          {/* Overlay */}
          <motion.div
            key="overlay"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={close}
            className="fixed inset-0 z-[100]"
            style={{
              background: 'rgba(5, 6, 26, 0.7)',
              backdropFilter: 'blur(8px)',
            }}
          />

          {/* Panel */}
          <motion.aside
            key="panel"
            variants={panelVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed right-0 top-0 z-[101] flex h-[100svh] w-full max-w-[420px] flex-col"
            style={{
              background:
                'linear-gradient(160deg, #05061a 0%, #0d1235 50%, #121a45 100%)',
              boxShadow: '-24px 0 48px -12px rgba(0,0,0,0.6)',
              borderLeft: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-white/[0.06] px-6 py-5">
              <Logo variant="compact" />
              <button
                type="button"
                onClick={close}
                aria-label="Close menu"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white transition-colors hover:bg-white/5"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <motion.nav
              variants={listVariants}
              initial="hidden"
              animate="visible"
              className="flex-1 overflow-y-auto px-6 py-8"
            >
              <motion.p
                variants={itemVariants}
                className="text-label-mono mb-5"
                style={{ color: 'var(--color-gold)' }}
              >
                Shop
              </motion.p>
              <ul className="mb-10 space-y-1">
                {NAV_ITEMS.map((item) => (
                  <motion.li key={item.to} variants={itemVariants}>
                    <Link
                      to={item.to}
                      onClick={close}
                      className="group flex items-center justify-between rounded-md px-3 py-3.5 transition-colors hover:bg-white/[0.04]"
                    >
                      <span
                        className="font-[family-name:var(--font-display)] text-[26px] uppercase tracking-[0.02em]"
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {item.label}
                      </span>
                      <span
                        aria-hidden
                        className="opacity-30 transition-all group-hover:translate-x-1 group-hover:opacity-100"
                        style={{ color: 'var(--color-brand-magenta)' }}
                      >
                        →
                      </span>
                    </Link>
                  </motion.li>
                ))}
              </ul>

              <motion.p
                variants={itemVariants}
                className="text-label-mono mb-5"
                style={{ color: 'var(--color-gold)' }}
              >
                Account
              </motion.p>
              <ul className="space-y-1">
                {ACCOUNT_ITEMS.map((item) => (
                  <motion.li key={item.to} variants={itemVariants}>
                    <Link
                      to={item.to}
                      onClick={close}
                      className="block rounded-md px-3 py-2.5 text-[15px] text-[color:var(--color-text-secondary)] transition-colors hover:bg-white/[0.04] hover:text-white"
                    >
                      {item.label}
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.nav>

            {/* Footer (auth state) */}
            <div className="border-t border-white/[0.06] px-6 py-5">
              {isAuthenticated && user ? (
                <p
                  className="text-[13px]"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  Signed in as{' '}
                  <span className="text-white">{user.name ?? user.email}</span>
                </p>
              ) : (
                <div className="flex gap-3">
                  <Link
                    to="/login"
                    onClick={close}
                    className="flex-1 rounded-md border border-[color:var(--color-brand-magenta)] py-2.5 text-center text-[14px] text-[color:var(--color-brand-magenta)] hover:bg-[color:var(--color-brand-magenta)]/10"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    onClick={close}
                    className="flex-1 rounded-md bg-[color:var(--color-brand-magenta)] py-2.5 text-center text-[14px] text-white hover:bg-[color:var(--color-brand-magenta-light)]"
                  >
                    Register
                  </Link>
                </div>
              )}
            </div>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}
